import moment from "moment";
import React, { useEffect, useState } from "react";
import { getTopResidual } from "../../../../../api";
import MenuIcon40 from "../../../../assets/svg/icons/common/MenuIcon40";
import TrendDown24 from "../../../../assets/svg/icons/common/TrendDown24";
import TrendUp24 from "../../../../assets/svg/icons/common/TrendUp24";
import SkeletonLoader from "../../../../components/skeleton-loader";
import useDashboardStore from "../../../../stores/dashboard";
import MerchantNameLink from "../../../../components/MerchantNameLink";
import styles from "./style.module.scss";

const formatNumber = (num) => {
  if (typeof num !== "number" || isNaN(num)) {
    return "";
  }
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + "B";
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
};

const TopResidual = () => {
  const [topResidual, setTopResidual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const filterStartDate = useDashboardStore((s) => s.filterData.startDate);
  const filterEndDate = useDashboardStore((s) => s.filterData.endDate);
  const filterOrgId = useDashboardStore((s) => s.filterData.filterOrg);
  const lastMonthStartDate = moment()
    .subtract(1, "month")
    .startOf("month")
    .format("YYYY-MM-DD");
  const lastMonthEndDate = moment()
    .subtract(1, "month")
    .endOf("month")
    .format("YYYY-MM-DD");

  useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const res = await getTopResidual({
          start_date: lastMonthStartDate,
          end_date: lastMonthEndDate,
        });
        if (!cancelled && res.status) {
          setTopResidual(res.data);
          setIsInitialLoad(false);
        }
      } catch (error) {
        // A rejection here used to leave setLoading(true) in place, so the
        // widget sat on its skeleton forever. Show the empty state instead;
        // isInitialLoad stays true so a 403 is not re-requested on every
        // filter change.
        if (!cancelled) setTopResidual([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isInitialLoad) return;
    let cancelled = false;
    const fetchFiltered = async () => {
      setLoading(true);
      try {
        const res = await getTopResidual({
          start_date: filterStartDate,
          end_date: filterEndDate,
          org_id: filterOrgId,
        });
        if (!cancelled && res.status) {
          setTopResidual(res.data);
        }
      } catch (error) {
        if (!cancelled) setTopResidual([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFiltered();
    return () => {
      cancelled = true;
    };
  }, [filterStartDate, filterEndDate, filterOrgId, isInitialLoad]);

  return (
    <div className={styles.activeMerchant}>
      <div className={styles.activeMerchantHeader}>
        <div className={styles.activeMerchantHeaderContent}>
          <p className={styles.activeMerchantHeaderTitle}>Top Residual</p>
          <p className={styles.activeMerchantHeaderDate}>
            {moment(isInitialLoad ? lastMonthStartDate : filterStartDate)
              .subtract(1, "month")
              .startOf("month")
              .toDate()
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })}{" "}
            -{" "}
            {moment(isInitialLoad ? lastMonthEndDate : filterEndDate)
              .subtract(1, "month")
              .endOf("month")
              .toDate()
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })}
          </p>
        </div>
      </div>

      {loading && (
        <div className={styles.activeMerchantList}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={styles.activeMerchantListItem}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  width: "100%",
                }}
              >
                <SkeletonLoader
                  variant="rectangular"
                  height="20px"
                  width="100%"
                  style={{ borderRadius: "8px" }}
                />
                <SkeletonLoader
                  variant="rectangular"
                  height="16px"
                  width="75px"
                  style={{ borderRadius: "8px" }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <>
          {topResidual?.length > 0 ? (
            <div className={styles.activeMerchantList}>
              {topResidual.map((merchant, index) => (
                <div key={index} className={styles.activeMerchantListItem}>
                  <div className={styles.activeMerchantListItemContent}>
                    <p className={styles.activeMerchantListItemTitle}>
                      <MerchantNameLink powerappId={merchant?.powerapp_id}>
                        {merchant?.dba}
                      </MerchantNameLink>
                    </p>
                  </div>

                  <div className={styles.amountContainer}>
                    <p className={styles.amountValue}>
                      {merchant?.total !== undefined && merchant?.total !== null
                        ? `$${formatNumber(merchant.total)}`
                        : "--"}
                    </p>
                  </div>

                  <div className={styles.trendContainer}>
                    <span className={styles.trendIcon}>
                      {merchant?.trend == "up" ? (
                        <TrendUp24 />
                      ) : (
                        <TrendDown24 />
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyContainer}>
              <div className={styles.iconBox}>
                <MenuIcon40 />
              </div>

              <p className={styles.emptyTitle}>
                This card will display merchants generating the highest residual
                income during the selected period
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopResidual;
